import { TextInput } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";
import { formatHtmlInputDateString } from "../../app/routes/admin/selectors";

export type TMcInputNew = React.InputHTMLAttributes<HTMLInputElement> & {
  forceBlur?: boolean;
  normalizeNumbers?: boolean;
  [key: string]: unknown;
};

const McInputNew: React.FC<TMcInputNew> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onKeyUp,
  type = "text",
  pattern,
  className,
  size,
  forceBlur = false,
  normalizeNumbers = false,
  ...props
}) => {
  const { styles, ...inputProps } = props;
  const [internalValue, setInternalValue] = useState<string>(String(value) || "");
  let propagateValueOnBlur = useRef(true);

  useEffect(() => {
    setInternalValue(value == null ? "" : String(value));
  }, [value]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputProps.autoFocus != null && inputProps.autoFocus !== false) {
      inputRef.current?.focus();

      if (inputRef.current != null) {
        const v = inputRef.current.value;
        inputRef.current.value = "";
        inputRef.current.value = v;
      }
    }
  }, []);

  const checkKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    propagateValueOnBlur.current = true;

    if (e.key === "Enter") {
      onKeyDown?.(e);
      (e.target as HTMLElement).blur();
      return;
    }

    if (e.key === "Escape") {
      e.stopPropagation();
      propagateValueOnBlur.current = forceBlur;
      setInternalValue(value != null ? value?.toString() : "");
      (e.target as HTMLElement).blur();
      return;
    }

    onKeyDown?.(e as React.KeyboardEvent<HTMLInputElement>);
  };

  const stopEscOnKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      return;
    }

    onKeyUp?.(e as React.KeyboardEvent<HTMLInputElement>);
  };

  const valueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (normalizeNumbers === true) {
      e.target.value = e.target.value.replace(",", ".");
    }

    if (
      onChange != null &&
      e.target.value !== internalValue &&
      (pattern != null ? e.target.value.match(pattern) != null : true)
    ) {
      if (normalizeNumbers !== true) {
        onChange(e);
      }
      // Do not run onChange for normalizeNumbers when value is not numeric.
      else if (isNaN(Number(e.target.value)) === false) {
        onChange(e);
      }
    }

    setInternalValue(e.target.value || "");
  };

  const blurOverride = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      onBlur != null &&
      e.target.validity.valid === true &&
      propagateValueOnBlur.current === true &&
      (value == null ? "" : String(value)) !== internalValue &&
      (pattern != null ? e.target.value.match(pattern) != null : true)
    ) {
      onBlur(e);
    }
  };

  const inputClassName = `mcinput${className != null ? ` ${className}` : ""}`;
  const inputSize = typeof size === "string" ? size : "sm";
  const labelStyles = {
    fontWeight: 600,
    fontSize: "var(--mantine-font-size-sm)",
    marginBottom: 0,
  };
  const mantineStyles = styles as Record<string, unknown> | undefined;

  return (
    <TextInput
      {...inputProps}
      size={inputSize}
      ref={inputRef}
      classNames={{ input: inputClassName }}
      styles={{ ...mantineStyles, label: { ...labelStyles, ...(mantineStyles?.label as object | undefined) } }}
      type={type}
      value={type === "date" ? formatHtmlInputDateString(internalValue) : internalValue}
      onChange={valueChanged}
      onBlur={blurOverride}
      onKeyDown={checkKey}
      onKeyUp={stopEscOnKeyUp}
      onWheel={() => {
        if (type === "number" && inputRef.current?.blur) {
          inputRef.current.blur();
        }
      }}
    />
  );
};

export type TMcInput = TMcInputNew;

export default McInputNew;
